import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

import Header from '../components/Header';
import Button from '../components/Button';
import QualifierReport from '../components/QualifierReport';
import { getUserPrograms, getProgram } from '../services/api';
import { colors, statusColors } from '../theme';
import { toIST } from '../utils/dateUtils';

const PRIMARY = '#6332D1';
const TEXT = {
  heading: '#251A3E',
  body: '#524B70',
  muted: '#7B7394',
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #F5F3FF;
  color: ${TEXT.body};
`;

const Content = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  animation: ${fadeIn} 0.5s ease-out;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 2rem 3rem;
  }

  @media (min-width: 1200px) {
    padding: 2rem 4rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${TEXT.heading};
  position: relative;
  display: inline-block;
  letter-spacing: -0.02em;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${PRIMARY};
    border-radius: 2px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #E8E0F5;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      height: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: rgba(159, 118, 249, 0.3);
      border-radius: 3px;
    }
  }
`;

const Tab = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? PRIMARY : 'transparent'};
  color: ${props => props.active ? PRIMARY : TEXT.muted};
  font-weight: ${props => props.active ? '600' : '500'};
  margin-right: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.active ? PRIMARY : TEXT.heading};
    background-color: rgba(99, 50, 209, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const ProgramCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
  display: flex;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  opacity: ${props => props.disabled ? 0.6 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  position: relative;
  
  ${props => props.disabled && `
    &::after {
      content: 'Program Inactive';
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: #ff5252;
      color: white;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 600;
      z-index: 10;
    }
  `}
  
  &:hover {
    box-shadow: ${props => props.disabled ? '0 4px 12px rgba(0, 0, 0, 0.05)' : '0 8px 20px rgba(0, 0, 0, 0.1)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-3px)'};
    border-color: ${props => props.disabled ? 'transparent' : 'rgba(99, 50, 209, 0.22)'};
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProgramImage = styled.div`
  width: 200px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(159, 118, 249, 0.1), transparent);
  }
  
  img {
    max-width: 100%;
    max-height: 50%;
    transition: transform 0.3s ease;
  }
  
  ${ProgramCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 120px;
  }
`;

const ProgramContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const ProgramHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const ProgramTitle = styled.h3`
  margin: 0;
  color: ${TEXT.heading};
  font-size: 1.4rem;
  transition: color 0.3s ease;
  letter-spacing: -0.01em;
  
  ${ProgramCard}:hover & {
    color: ${PRIMARY};
  }
`;

const ProgramStatus = styled.span`
  background-color: ${props => {
    switch (props.children?.toLowerCase()) {
      case 'enrolled': return 'rgba(99, 50, 209, 0.1)';
      case 'test completed': return '#e3f2fd';
      case 'counseling booked': return '#e8f5e9';
      default: return '#fff8e1';
    }
  }};
  color: ${props => {
    switch (props.children?.toLowerCase()) {
      case 'enrolled': return PRIMARY;
      case 'test completed': return '#1565c0';
      case 'counseling booked': return '#2e7d32';
      default: return '#f57c00';
    }
  }};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  ${ProgramCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProgramInstitution = styled.p`
  color: ${TEXT.body};
  margin: 0.25rem 0 1rem 0;
  display: flex;
  align-items: center;
  font-weight: 500;
  
  &:before {
    content: '🏛️';
    margin-right: 0.5rem;
    font-size: 1.1rem;
  }
`;

const ProgramDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #F9F7FF;
  border-radius: 8px;
  border: 1px solid #EBE4F8;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatusContainer = styled.div`
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: unset;
  }
`;

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: unset;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  color: ${TEXT.body};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(3px);
  }
  
  svg {
    margin-right: 0.5rem;
    color: ${PRIMARY};
  }

  strong {
    color: ${TEXT.heading};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: auto;
  align-items: center;
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 1rem;
  }
`;

const StyledButton = styled(Button)`
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${TEXT.muted};
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(99, 50, 209, 0.12);
  border-radius: 50%;
  border-top: 4px solid ${PRIMARY};
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 2rem 0;
  
  h3 {
    color: ${TEXT.heading};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${TEXT.body};
    margin-bottom: 2rem;
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: ${props => {
    if (props.completed) return statusColors.completed.text;
    if (props.pending) return statusColors.pending.text;
    return statusColors.inactive.text;
  }};
`;

const StatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => {
    if (props.completed) return statusColors.completed.background;
    if (props.pending) return statusColors.pending.background;
    return statusColors.inactive.background;
  }};
  
  svg {
    width: 12px;
    height: 12px;
    color: ${props => {
      if (props.completed) return statusColors.completed.text;
      if (props.pending) return statusColors.pending.text;
      return statusColors.inactive.text;
    }};
  }
`;

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('ongoing');
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQualifierReport, setShowQualifierReport] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserPrograms = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user?.user_id) {
          navigate('/login');
          return;
        }
        
        const userPrograms = await getUserPrograms(user.user_id);
        
        // Ensure we have program names for each program
        const programsWithDetails = await Promise.all(
          userPrograms.map(async (program) => {
            // If program name is missing, fetch it
            if (!program.name && program.program_id) {
              try {
                const programDetails = await getProgram(program.program_id);
                return { ...program, name: programDetails.name, institution: programDetails.institution };
              } catch (error) {
                console.error(`Error fetching details for program ${program.program_id}:`, error);
                return program;
              }
            }
            return program;
          })
        );
        
        setPrograms(programsWithDetails);
      } catch (error) {
        console.error('Error fetching programs:', error);
        toast.error('Failed to load your programs. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserPrograms();
  }, [navigate]);

  const handleCloseQualifierReport = () => {
    setShowQualifierReport(false);
  };

  const handleViewProgram = (program) => {
    const counselingBooking = program.counseling_booking;
    const hasTestBooking = !!program.test_booking_id;
    const testCompleted = program.test_status === "completed";
    const isGandhinagarProgram = parseInt(program.program_id) === 2;

    // For Gandhinagar program (ID 2), disable counseling - only allow test booking
    if (isGandhinagarProgram) {
      if (hasTestBooking) {
        navigate(`/test-success/${program.test_booking_id}`);
      } else {
        navigate(`/book-slot/${program.program_id}`);
      }
      return;
    }

    // Normal flow for other programs
    if(counselingBooking){
      navigate(`/book-counseling/${program.test_booking_id}`);
    } else if (hasTestBooking && testCompleted) {
      // If test is completed but no counseling, go to book counseling
      navigate(`/test-success/${program.test_booking_id}`);
    } else if (hasTestBooking) {
      // If test is booked but not completed, go to test details
      navigate(`/test-success/${program.test_booking_id}`);
    } else {
      // If nothing is booked yet, go to book test slot
      navigate(`/book-slot/${program.program_id}`);
    }
  };

  const renderProgramButtons = (program) => {
    const counselingBooking = program.counseling_booking;
    const hasTestBooking = !!program.test_booking_id;
    const testCompleted = program.test_status === "completed";
    
    // Determine button text based on program status
    let buttonText = "Book Now";
    let variant = "primary";
    
    if (counselingBooking) {
      buttonText = counselingBooking.can_join ? "Join Meeting" : "View Counseling";
    } else if (hasTestBooking && testCompleted) {
      buttonText = "Click here";
    } else if (hasTestBooking) {
      buttonText = "View Test Details";
      variant = "secondary";
    }
    
    return (
      <>
        <StyledButton 
          onClick={() => handleViewProgram(program)}
          variant={variant}
        >
          {buttonText}
        </StyledButton>
      </>
    );
  };

  const renderPrograms = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading your programs...</p>
        </LoadingContainer>
      );
    }

    if (programs.length === 0) {
      return (
        <EmptyState>
          <h3>Uh oh! No Programs Found</h3>
          <p>You are not enrolled in any programs yet. Please ask your counselor to enroll you in a program.</p>
          
        </EmptyState>
      );
    }

    return programs.map((program) => {
      // Check if there's a counseling booking for this program
      const counselingBooking = program.counseling_booking;
      const hasTestBooking = !!program.test_booking_id;
      const testCompleted = program.test_status === "completed";
      const isInactive = program.is_active === false;
      
      return (
        <ProgramCard key={program.program_id} disabled={isInactive}>
          <ProgramImage>
            {program.program_id === 1 && (
              <img 
                src={`../../assets/iitm x cce logo1.png`} 
                alt={program.name} 
              />
            )}
            {program.program_id === 2 && (
              <img 
                src={`../../assets/iit-gandhinagar x cdf logo.png`} 
                alt={program.name} 
              />
            )}
            {program.program_id === 3 && (
              <img 
                src={`../../assets/iitjodhpur-logo.png`} 
                alt={program.name} 
              />
            )}
          </ProgramImage>
          <ProgramContent>
            <ProgramHeader>
              <ProgramTitle>{program.name}</ProgramTitle>
              <ProgramStatus>{program.status || 'Enrolled'}</ProgramStatus>
            </ProgramHeader>
            <ProgramInstitution>{program.university}</ProgramInstitution>
            
            <ProgramDetails>
              <StatusContainer>
                <StatusIndicator completed={hasTestBooking}>
                  <StatusIcon completed={hasTestBooking}>
                    {hasTestBooking ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                      </svg>
                    )}
                  </StatusIcon>
                  Test Booking: {hasTestBooking ? 'Completed' : 'Pending'}
                </StatusIndicator>
                
                <StatusIndicator completed={testCompleted} pending={hasTestBooking && !testCompleted}>
                  <StatusIcon completed={testCompleted} pending={hasTestBooking && !testCompleted}>
                    {testCompleted ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    ) : hasTestBooking ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                      </svg>
                    )}
                  </StatusIcon>
                  Test Completion: {testCompleted ? 'Completed' : hasTestBooking ? 'Pending' : 'Not Started'}
                </StatusIndicator>

                {program.program_id === 1 && (
                <StatusIndicator completed={!!counselingBooking} pending={testCompleted && !counselingBooking}>
                  <StatusIcon completed={!!counselingBooking} pending={testCompleted && !counselingBooking}>
                    {counselingBooking ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    ) : testCompleted ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                      </svg>
                    )}
                  </StatusIcon>
                  Counseling: {counselingBooking ? 'Booked' : testCompleted ? 'Pending' : 'Not Available'}
                </StatusIndicator>
                )}
                
                {testCompleted && program.test_score !== null && (
                  <DetailItem>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Test Score: {program.test_score || 'Not available'}
                  </DetailItem>
                )}
              </StatusContainer>
              
              <DateContainer>
                {hasTestBooking && program.selected_slot && (
                  <DetailItem>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <div>
                      <strong style={{ fontSize: '14px' }}>Test Date:</strong><br/>
                      {new Date(program.selected_slot).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                      })}
                    </div>
                  </DetailItem>
                )}
                
                {counselingBooking && (
                  <DetailItem>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <div>
                      <strong style={{ fontSize: '14px' }}>Counseling Date:</strong><br/>
                      {toIST(counselingBooking.timeslot).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                      })}
                    </div>
                  </DetailItem>
                )}
              </DateContainer>
            </ProgramDetails>
            
            <ButtonContainer>
              {renderProgramButtons({
                ...program,
                counseling_booking: counselingBooking
              })}
            </ButtonContainer>
          </ProgramContent>
        </ProgramCard>
      );
    });
  };

  return (
    <DashboardContainer>
      <Header />
      <Content>
        <Title>Dashboard</Title>
        
        <TabsContainer>
          <Tab 
            active={activeTab === 'ongoing'} 
            onClick={() => setActiveTab('ongoing')}
          >
            Ongoing Programs
          </Tab>
          {/* <Tab 
            active={activeTab === 'completed'} 
            onClick={() => setActiveTab('completed')}
          >
            Completed Programs
          </Tab> */}
        </TabsContainer>
        
        {renderPrograms()}
      </Content>

      {/* Qualifier Report Modal */}
      <QualifierReport 
        open={showQualifierReport} 
        onClose={handleCloseQualifierReport} 
        bookingId={selectedBookingId} 
      />
    </DashboardContainer>
  );
};

export default Dashboard;
